/**
 * BloqueosRecurrentesEditor — CRUD de bloqueos recurrentes de la sala.
 *
 * Bloqueos = rangos horarios que se RESTAN de la disponibilidad y se repiten
 * cada semana en uno o más días (ej. almuerzo L-V 12:00-13:00, reunión los
 * lunes 08:00-09:00). Tienen fecha de inicio, uno o más días y fecha de fin
 * opcional. Distinto de las suspensiones, que son excepciones puntuales por
 * fecha.
 *
 * Datos: subcolección `salas/{salaId}/bloqueos`.
 */
import { useState, useEffect } from 'react';
import { collection, onSnapshot, Timestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Plus, Trash2, Lock } from 'lucide-react';
import { db, functions } from '../../config/firebase';
import { Input } from '../ui/Input';
import { useToast } from '../../contexts/ToastContext';
import styles from './BloqueosRecurrentesEditor.module.css';

interface Bloqueo {
  id: string;
  diasSemana: number[];
  horaInicio: string;
  horaFin: string;
  motivo: string;
  vigenciaDesde?: Timestamp;
  vigenciaHasta?: Timestamp | null;
}

// Días en orden de la semana (Lun → Dom) para el selector y las etiquetas.
const DIAS_ORDEN: Array<{ v: number; l: string }> = [
  { v: 1, l: 'Lun' },
  { v: 2, l: 'Mar' },
  { v: 3, l: 'Mié' },
  { v: 4, l: 'Jue' },
  { v: 5, l: 'Vie' },
  { v: 6, l: 'Sáb' },
  { v: 0, l: 'Dom' },
];

const callCrear = httpsCallable(functions, 'portal_crearBloqueoRecurrente');
const callEliminar = httpsCallable(functions, 'portal_eliminarBloqueoRecurrente');

function hoyIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function tsToIso(ts?: Timestamp | null): string | null {
  if (!ts) return null;
  try { return ts.toDate().toISOString().slice(0, 10); } catch { return null; }
}

function nombresDias(dias: number[]): string {
  return DIAS_ORDEN.filter((d) => dias.includes(d.v)).map((d) => d.l).join(', ');
}

export interface BloqueosRecurrentesEditorProps {
  salaId: string;
}

export function BloqueosRecurrentesEditor({ salaId }: BloqueosRecurrentesEditorProps) {
  const { addToast } = useToast();
  const [items, setItems] = useState<Bloqueo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    diasSemana: [1, 2, 3, 4, 5] as number[],
    horaInicio: '12:00',
    horaFin: '13:00',
    motivo: '',
    vigenciaDesde: hoyIso(),
    sinFin: true,
    vigenciaHasta: hoyIso(),
  });

  useEffect(() => {
    if (!salaId) return;
    const ref = collection(db, `salas/${salaId}/bloqueos`);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list: Bloqueo[] = snap.docs.map((d) => ({
          id: d.id,
          ...(d.data() as Omit<Bloqueo, 'id'>),
        }));
        list.sort((a, b) => a.horaInicio.localeCompare(b.horaInicio));
        setItems(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
    return () => unsub();
  }, [salaId]);

  const resetForm = () => {
    setForm({
      diasSemana: [1, 2, 3, 4, 5],
      horaInicio: '12:00',
      horaFin: '13:00',
      motivo: '',
      vigenciaDesde: hoyIso(),
      sinFin: true,
      vigenciaHasta: hoyIso(),
    });
  };

  const toggleDia = (v: number) => {
    setForm((f) => ({
      ...f,
      diasSemana: f.diasSemana.includes(v)
        ? f.diasSemana.filter((d) => d !== v)
        : [...f.diasSemana, v],
    }));
  };

  // Sin <form> porque puede vivir dentro de otro form (SalaFormPage).
  const handleSubmit = async () => {
    if (form.diasSemana.length === 0) {
      addToast('error', 'Seleccione al menos un día');
      return;
    }
    if (form.motivo.trim().length < 3) {
      addToast('error', 'Motivo requerido (mín. 3 caracteres)');
      return;
    }
    if (form.horaInicio >= form.horaFin) {
      addToast('error', 'La hora de inicio debe ser anterior a la de fin');
      return;
    }
    setSubmitting(true);
    try {
      await callCrear({
        salaId,
        diasSemana: form.diasSemana,
        horaInicio: form.horaInicio,
        horaFin: form.horaFin,
        motivo: form.motivo,
        vigenciaDesde: form.vigenciaDesde,
        vigenciaHasta: form.sinFin ? null : form.vigenciaHasta,
      });
      addToast('success', 'Bloqueo recurrente creado');
      resetForm();
      setShowForm(false);
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (bloqueoId: string) => {
    try {
      await callEliminar({ salaId, bloqueoId });
      addToast('success', 'Bloqueo eliminado');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Error');
    }
  };

  const formatVigencia = (b: Bloqueo): string => {
    const desde = tsToIso(b.vigenciaDesde);
    const hasta = tsToIso(b.vigenciaHasta);
    if (!desde) return '';
    return hasta ? `${desde} → ${hasta}` : `desde ${desde} (sin fin)`;
  };

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.addBtn}
          onClick={() => setShowForm((v) => !v)}
        >
          <Plus size={14} /> {showForm ? 'Cancelar' : 'Agregar bloqueo'}
        </button>
      </div>

      {showForm && (
        <div className={styles.formCard}>
          <div>
            <span className={styles.fieldLabel}>Días *</span>
            <div className={styles.diasPicker}>
              {DIAS_ORDEN.map((d) => (
                <button
                  key={d.v}
                  type="button"
                  className={`${styles.diaChip} ${form.diasSemana.includes(d.v) ? styles.diaChipActive : ''}`}
                  onClick={() => toggleDia(d.v)}
                >
                  {d.l}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.formGrid}>
            <Input
              label="Hora inicio *"
              type="time"
              value={form.horaInicio}
              onChange={(e) => setForm({ ...form, horaInicio: e.target.value })}
              required
            />
            <Input
              label="Hora fin *"
              type="time"
              value={form.horaFin}
              onChange={(e) => setForm({ ...form, horaFin: e.target.value })}
              required
            />
            <Input
              label="Motivo *"
              placeholder="Ej. Almuerzo, Reunión general"
              value={form.motivo}
              onChange={(e) => setForm({ ...form, motivo: e.target.value })}
              required
            />
            <Input
              label="Vigente desde *"
              type="date"
              value={form.vigenciaDesde}
              onChange={(e) => setForm({ ...form, vigenciaDesde: e.target.value })}
              required
            />
            {!form.sinFin && (
              <Input
                label="Vigente hasta"
                type="date"
                value={form.vigenciaHasta}
                onChange={(e) => setForm({ ...form, vigenciaHasta: e.target.value })}
              />
            )}
          </div>

          <label className={styles.checkRow}>
            <input
              type="checkbox"
              checked={form.sinFin}
              onChange={(e) => setForm({ ...form, sinFin: e.target.checked })}
            />
            <span>Sin fecha de fin (indefinido)</span>
          </label>

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPri}
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Guardando…' : 'Registrar bloqueo'}
            </button>
          </div>
        </div>
      )}

      <div className={styles.list}>
        {loading ? (
          <p className={styles.empty}>Cargando…</p>
        ) : items.length === 0 ? (
          <p className={styles.empty}>Sin bloqueos recurrentes registrados.</p>
        ) : (
          items.map((b) => (
            <div key={b.id} className={styles.item}>
              <div className={styles.itemInfo}>
                <span className={styles.itemFecha}>
                  <Lock size={12} className={styles.itemIcon} />
                  {nombresDias(b.diasSemana)} · {b.horaInicio}–{b.horaFin}
                </span>
                <span className={styles.itemMotivo}>
                  {b.motivo} · {formatVigencia(b)}
                </span>
              </div>
              <button
                type="button"
                className={styles.delBtn}
                onClick={() => handleDelete(b.id)}
                title="Eliminar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
